import type { ReactElement, ReactNode } from "react"
import styled from "styled-components"
import { useAuth } from "~/features/auth/useAuth"
import { ThemeToggle } from "~/shared/ui"

const Frame = styled.div`
  min-height: 100vh;
  padding: ${({ theme }) => theme.spacing.lg};
  max-width: 76rem;
  margin: 0 auto;
`

const Bar = styled.header`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`

const SignedInAs = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
`

/**
 * Deliberately plain. In production the host shell supplies the header, left nav,
 * breadcrumbs and footer; this exists only so standalone dev mode has somewhere to
 * mount, and should never be mistaken for real chrome.
 *
 * The theme toggle is the exception to "plain": it is the only control this bar
 * owns rather than borrows, and it renders signed out as well as in, since the
 * sign-in screen is drawn in the same palette as everything else.
 */
export const DevShell = ({ children }: { readonly children: ReactNode }): ReactElement => {
  const { status, displayName, signOut } = useAuth()

  return (
    <Frame>
      <Bar>
        {status === "signedIn" ? (
          <SignedInAs>
            {displayName ?? "Signed in"}
            {" · "}
            <button type="button" data-qa-hook="dev-shell-sign-out" onClick={() => void signOut()}>
              Sign out
            </button>
          </SignedInAs>
        ) : null}
        <ThemeToggle qaHook="dev-shell-theme-toggle" />
      </Bar>
      {children}
    </Frame>
  )
}
